import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  format, subDays, startOfMonth, startOfDay, endOfDay,
  startOfWeek, endOfWeek,
} from "date-fns";
import {
  Users, Target, Phone, Mail, Bell, MonitorSmartphone, FileText,
  TrendingUp, TrendingDown, Minus, CalendarDays, ChevronUp, ChevronDown,
  Loader2, Activity, Clock, Timer, Download, Circle, RotateCcw,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ActivityUserDrillDown } from "./activity-user-drill-down";

interface ActivitySummary {
  period: { start: string; end: string };
  metrics: {
    newLeads: number;
    clientsCreated: number;
    callsLogged: number;
    remindersSet: number;
    emailsSent: number;
    massEmailsSent: number;
    activeDripClients: number;
    siteSessions: number;
    invoicesResent: number;
    conversionRate: number;
    totalActiveTimeMs: number;
    avgSessionLengthMs: number;
    sessionsRolled: number;
  };
  priorMetrics?: any;
  percentChanges?: Record<string, number>;
  goals: Record<string, { daily?: number; weekly?: number; monthly?: number }>;
}

interface UserActivity {
  userId: string;
  userName: string;
  email: string;
  role: string;
  leads: number;
  clients: number;
  calls: number;
  emails: number;
  reminders: number;
  sessions: number;
  totalActiveTimeMs: number;
  avgSessionLengthMs: number;
  sessionsRolled: number;
  isOnline: boolean;
  lastActiveAt: string | null;
  totalActivity: number;
  conversionRate: number;
  lastActivityType: string | null;
  lastActivityDate: string | null;
  inactivityDays: number;
  inactivityBucket: string;
}

interface UsersResponse {
  users: UserActivity[];
  inactivityCounters: Record<string, number>;
  period: { start: string; end: string };
}

interface HeatmapResponse {
  dates: string[];
  users: Array<{ userId: string; userName: string; days: Record<string, number> }>;
}

interface TrendsResponse {
  dates: string[];
  leads: number[];
  clients: number[];
  calls: number[];
  emails: number[];
  sessions: number[];
}

interface TimeOfDayResponse {
  period: { start: string; end: string };
  grid: number[][]; // [dayOfWeek (0=Sun)][hour 0..23] = active minutes
}

interface DevicesResponse {
  period: { start: string; end: string };
  totalSessions: number;
  browsers: Array<{ name: string; count: number; share: number }>;
  devices: Array<{ name: string; count: number; share: number }>;
}

interface HeartbeatStatsResponse {
  today: { date: string; created: number; updated: number; rolled: number };
  days: Array<{ date: string; created: number; updated: number; rolled: number }>;
}

interface TimecardSummaryResponse {
  totalClockedMs: number;
  perUser: Record<string, number>;
}

export type DateRange =
  | "today"
  | "yesterday"
  | "this-week"
  | "7d"
  | "this-month"
  | "30d"
  | "custom";

type SortField =
  | "userName"
  | "totalActivity"
  | "leads"
  | "clients"
  | "calls"
  | "emails"
  | "sessions"
  | "totalActiveTimeMs"
  | "avgSessionLengthMs"
  | "conversionRate"
  | "inactivityDays";

function formatActiveMs(ms: number): string {
  if (!ms || ms < 1000) return "0m";
  const totalMin = Math.round(ms / 60000);
  if (totalMin < 60) return `${totalMin}m`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function formatSessionMs(ms: number): string {
  if (!ms || ms < 1000) return "—";
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm ? `${h}h ${mm}m` : `${h}h`;
}

const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Compact 7-day heartbeat-outcome panel: today's badges + a tiny stacked bar
// chart of created vs. updated vs. rolled. Kept inline so it can read
// HeartbeatStatsResponse without prop-drilling extra types into a shared file.
function HeartbeatOutcomesCard({
  data,
  isLoading,
}: {
  data: HeartbeatStatsResponse | undefined;
  isLoading: boolean;
}) {
  const today = data?.today;
  const days = data?.days ?? [];
  const maxTotal = Math.max(
    1,
    ...days.map(d => d.created + d.updated + d.rolled),
  );

  const dayLabel = (iso: string) => {
    // Parse YYYY-MM-DD as a local date so we don't shift by timezone.
    const [y, m, d] = iso.split("-").map(n => parseInt(n, 10));
    const dt = new Date(y, (m || 1) - 1, d || 1);
    return DAYS_OF_WEEK[dt.getDay()];
  };

  return (
    <Card data-testid="card-heartbeat-outcomes">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center justify-between gap-4 flex-wrap">
          <span>Heartbeat Outcomes</span>
          <div className="flex gap-2 flex-wrap">
            <Badge variant="outline" className="bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/30" data-testid="badge-heartbeat-today-created">
              Sessions created today: {today?.created ?? 0}
            </Badge>
            <Badge variant="outline" className="bg-teal-500/10 text-teal-600 dark:text-teal-400 border-teal-500/30" data-testid="badge-heartbeat-today-rolled">
              Sessions rolled today: {today?.rolled ?? 0}
            </Badge>
            <Badge variant="outline" className="bg-muted text-muted-foreground" data-testid="badge-heartbeat-today-updated">
              Heartbeats today: {today?.updated ?? 0}
            </Badge>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-2">
        {isLoading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : days.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="text-heartbeat-empty">
            No heartbeat data yet. Counts will start appearing as users load the app.
          </p>
        ) : (
          <div className="space-y-2" data-testid="chart-heartbeat-7d">
            <div className="flex items-end gap-2 h-24">
              {days.map(d => {
                const total = d.created + d.updated + d.rolled;
                const heightPct = (total / maxTotal) * 100;
                const createdPct = total > 0 ? (d.created / total) * 100 : 0;
                const rolledPct = total > 0 ? (d.rolled / total) * 100 : 0;
                const updatedPct = total > 0 ? 100 - createdPct - rolledPct : 0;
                return (
                  <div
                    key={d.date}
                    className="flex-1 flex flex-col justify-end h-full"
                    title={`${d.date} — created ${d.created}, rolled ${d.rolled}, heartbeats ${d.updated}`}
                    data-testid={`bar-heartbeat-${d.date}`}
                  >
                    <div
                      className="w-full rounded-sm overflow-hidden bg-muted flex flex-col-reverse"
                      style={{ height: `${Math.max(heightPct, total > 0 ? 4 : 0)}%` }}
                    >
                      <div className="bg-blue-500" style={{ height: `${createdPct}%` }} />
                      <div className="bg-teal-500" style={{ height: `${rolledPct}%` }} />
                      <div className="bg-muted-foreground/40" style={{ height: `${updatedPct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2 text-xs text-muted-foreground">
              {days.map(d => (
                <div key={`l-${d.date}`} className="flex-1 text-center">
                  {dayLabel(d.date)}
                </div>
              ))}
            </div>
            <div className="flex gap-4 text-xs text-muted-foreground pt-1">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-sm bg-blue-500 inline-block" /> Created
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-sm bg-teal-500 inline-block" /> Rolled
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-sm bg-muted-foreground/40 inline-block" /> Heartbeats
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface ActivityTrackerProps {
  dateRange?: DateRange;
  onDateRangeChange?: (v: DateRange) => void;
  customStart?: Date;
  onCustomStartChange?: (v: Date | undefined) => void;
  customEnd?: Date;
  onCustomEndChange?: (v: Date | undefined) => void;
  showComparison?: boolean;
  onShowComparisonChange?: (v: boolean) => void;
  userFilter?: string;
  onUserFilterChange?: (v: string) => void;
  start?: Date;
  end?: Date;
  hideFilterBar?: boolean;
}

export function ActivityTracker(props: ActivityTrackerProps = {}) {
  const {
    hideFilterBar = false,
  } = props;

  const [_dateRange, _setDateRange] = useState<DateRange>("7d");
  const [_customStart, _setCustomStart] = useState<Date | undefined>();
  const [_customEnd, _setCustomEnd] = useState<Date | undefined>();
  const [sortField, setSortField] = useState<SortField>("totalActivity");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [_showComparison, _setShowComparison] = useState(true);
  const [_userFilter, _setUserFilter] = useState<string>("all");
  const [drillDownUser, setDrillDownUser] = useState<{ userId: string; userName: string } | null>(null);

  const isControlled = props.dateRange !== undefined;
  const dateRange = isControlled ? props.dateRange! : _dateRange;
  const setDateRange = isControlled ? (props.onDateRangeChange ?? (() => {})) : _setDateRange;
  const customStart = isControlled ? props.customStart : _customStart;
  const setCustomStart = isControlled ? (props.onCustomStartChange ?? (() => {})) : _setCustomStart;
  const customEnd = isControlled ? props.customEnd : _customEnd;
  const setCustomEnd = isControlled ? (props.onCustomEndChange ?? (() => {})) : _setCustomEnd;
  const showComparison = isControlled ? (props.showComparison ?? true) : _showComparison;
  const setShowComparison = isControlled ? (props.onShowComparisonChange ?? (() => {})) : _setShowComparison;
  const userFilter = isControlled ? (props.userFilter ?? "all") : _userFilter;
  const setUserFilter = isControlled ? (props.onUserFilterChange ?? (() => {})) : _setUserFilter;

  const { start, end } = useMemo(() => {
    if (isControlled && props.start && props.end) {
      return { start: props.start, end: props.end };
    }
    const now = new Date();
    switch (dateRange) {
      case "today":
        return { start: startOfDay(now), end: endOfDay(now) };
      case "yesterday": {
        const y = subDays(now, 1);
        return { start: startOfDay(y), end: endOfDay(y) };
      }
      case "this-week":
        return { start: startOfWeek(now, { weekStartsOn: 0 }), end: endOfWeek(now, { weekStartsOn: 0 }) };
      case "7d":
        return { start: startOfDay(subDays(now, 7)), end: endOfDay(now) };
      case "this-month":
        return { start: startOfMonth(now), end: endOfDay(now) };
      case "30d":
        return { start: startOfDay(subDays(now, 30)), end: endOfDay(now) };
      case "custom":
        return {
          start: customStart || startOfDay(subDays(now, 7)),
          end: customEnd || endOfDay(now),
        };
      default:
        return { start: startOfDay(subDays(now, 7)), end: endOfDay(now) };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, customStart, customEnd, isControlled, props.start, props.end]);

  const userIdParam = userFilter !== "all" ? userFilter : undefined;

  const summaryQuery = useQuery<ActivitySummary>({
    queryKey: ["/api/admin/activity-tracker/summary", start.toISOString(), end.toISOString(), showComparison],
    queryFn: async () => {
      const params = new URLSearchParams({
        start: start.toISOString(),
        end: end.toISOString(),
        compare: showComparison.toString(),
      });
      const response = await fetch(`/api/admin/activity-tracker/summary?${params}`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch summary");
      return response.json();
    },
  });

  const usersQuery = useQuery<UsersResponse>({
    queryKey: ["/api/admin/activity-tracker/users", start.toISOString(), end.toISOString(), sortField, sortDir, userIdParam],
    queryFn: async () => {
      const params = new URLSearchParams({
        start: start.toISOString(),
        end: end.toISOString(),
        sort: sortField,
        dir: sortDir,
      });
      if (userIdParam) params.set("userId", userIdParam);
      const response = await fetch(`/api/admin/activity-tracker/users?${params}`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch users");
      return response.json();
    },
    refetchInterval: 60 * 1000, // refresh once a minute so "Currently Online" stays fresh
  });

  // Always-loaded users list (no filter) for the user filter dropdown
  const allUsersQuery = useQuery<UsersResponse>({
    queryKey: ["/api/admin/activity-tracker/users", start.toISOString(), end.toISOString(), "userName", "asc", "all"],
    queryFn: async () => {
      const params = new URLSearchParams({
        start: start.toISOString(),
        end: end.toISOString(),
        sort: "userName",
        dir: "asc",
      });
      const response = await fetch(`/api/admin/activity-tracker/users?${params}`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch users");
      return response.json();
    },
    enabled: !!userIdParam || true, // always load so filter dropdown is populated
  });

  const trendsQuery = useQuery<TrendsResponse>({
    queryKey: ["/api/admin/activity-tracker/trends"],
    queryFn: async () => {
      const response = await fetch("/api/admin/activity-tracker/trends?days=30", { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch trends");
      return response.json();
    },
  });

  const heatmapQuery = useQuery<HeatmapResponse>({
    queryKey: ["/api/admin/activity-tracker/heatmap", start.toISOString(), end.toISOString()],
    queryFn: async () => {
      const params = new URLSearchParams({ start: start.toISOString(), end: end.toISOString() });
      const response = await fetch(`/api/admin/activity-tracker/heatmap?${params}`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch heatmap");
      return response.json();
    },
  });

  const todHeatmapQuery = useQuery<TimeOfDayResponse>({
    queryKey: ["/api/admin/activity-tracker/time-of-day-heatmap", start.toISOString(), end.toISOString(), userIdParam],
    queryFn: async () => {
      const params = new URLSearchParams({ start: start.toISOString(), end: end.toISOString() });
      if (userIdParam) params.set("userId", userIdParam);
      const response = await fetch(`/api/admin/activity-tracker/time-of-day-heatmap?${params}`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch time-of-day heatmap");
      return response.json();
    },
  });

  const heartbeatStatsQuery = useQuery<HeartbeatStatsResponse>({
    queryKey: ["/api/admin/activity-tracker/heartbeat-stats"],
    queryFn: async () => {
      const response = await fetch("/api/admin/activity-tracker/heartbeat-stats", { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch heartbeat stats");
      return response.json();
    },
    refetchInterval: 60 * 1000,
  });

  const devicesQuery = useQuery<DevicesResponse>({
    queryKey: ["/api/admin/activity-tracker/devices", start.toISOString(), end.toISOString(), userIdParam],
    queryFn: async () => {
      const params = new URLSearchParams({ start: start.toISOString(), end: end.toISOString() });
      if (userIdParam) params.set("userId", userIdParam);
      const response = await fetch(`/api/admin/activity-tracker/devices?${params}`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch devices");
      return response.json();
    },
  });

  const timecardSummaryQuery = useQuery<TimecardSummaryResponse>({
    queryKey: ["/api/admin/timecard/summary", start.toISOString(), end.toISOString(), userIdParam],
    queryFn: async () => {
      const params = new URLSearchParams({ start: start.toISOString(), end: end.toISOString() });
      if (userIdParam) params.set("userId", userIdParam);
      const response = await fetch(`/api/admin/timecard/summary?${params}`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch timecard summary");
      return response.json();
    },
    refetchInterval: 60 * 1000,
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const handleExportCsv = () => {
    const params = new URLSearchParams({ start: start.toISOString(), end: end.toISOString() });
    if (userIdParam) params.set("userId", userIdParam);
    const url = `/api/admin/activity-tracker/export.csv?${params}`;
    // Trigger download (cookies are sent automatically with same-origin link)
    window.location.href = url;
  };

  const SortHeader = ({ field, children, title }: { field: SortField; children: React.ReactNode; title?: string }) => (
    <TableHead
      className="cursor-pointer hover-elevate select-none"
      onClick={() => handleSort(field)}
      data-testid={`sort-${field}`}
      title={title}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortField === field && (
          sortDir === "desc" ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />
        )}
      </div>
    </TableHead>
  );

  const MetricCard = ({
    title,
    value,
    icon: Icon,
    change,
    goal,
    color = "primary",
    testId,
  }: {
    title: string;
    value: number | string;
    icon: React.ElementType;
    change?: number;
    goal?: number;
    color?: string;
    testId?: string;
  }) => {
    const numericValue = typeof value === "number" ? value : 0;
    const progress = goal ? Math.min((numericValue / goal) * 100, 100) : 0;

    return (
      <Card className="relative overflow-visible" data-testid={testId}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={cn(
                "w-10 h-10 rounded-full flex items-center justify-center",
                color === "primary" && "bg-primary/10 text-primary",
                color === "green" && "bg-green-500/10 text-green-500",
                color === "blue" && "bg-blue-500/10 text-blue-500",
                color === "orange" && "bg-orange-500/10 text-orange-500",
                color === "purple" && "bg-purple-500/10 text-purple-500",
                color === "teal" && "bg-teal-500/10 text-teal-500",
              )}>
                <Icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{title}</p>
                <p className="text-2xl font-bold">{typeof value === "number" ? value.toLocaleString() : value}</p>
              </div>
            </div>
            {change !== undefined && (
              <div className={cn(
                "flex items-center gap-1 text-sm",
                change > 0 && "text-green-500",
                change < 0 && "text-red-500",
                change === 0 && "text-muted-foreground"
              )}>
                {change > 0 && <TrendingUp className="w-4 h-4" />}
                {change < 0 && <TrendingDown className="w-4 h-4" />}
                {change === 0 && <Minus className="w-4 h-4" />}
                <span>{change > 0 ? "+" : ""}{change}%</span>
              </div>
            )}
          </div>
          {goal !== undefined && goal > 0 && (
            <div className="mt-3">
              <div className="flex justify-between text-xs text-muted-foreground mb-1">
                <span>Goal: {goal}</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className={cn(
                    "h-full rounded-full transition-all",
                    progress >= 100 ? "bg-green-500" : "bg-primary"
                  )}
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const getInactivityColor = (bucket: string) => {
    switch (bucket) {
      case "active": return "bg-green-500/10 text-green-500";
      case "3+": return "bg-yellow-500/10 text-yellow-500";
      case "7+": return "bg-orange-500/10 text-orange-500";
      case "14+": return "bg-red-500/10 text-red-500";
      case "30+": return "bg-red-700/10 text-red-700";
      default: return "bg-muted text-muted-foreground";
    }
  };

  if (summaryQuery.isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const summary = summaryQuery.data;
  const users = usersQuery.data?.users || [];
  const allUsers = allUsersQuery.data?.users || [];
  const inactivityCounters = usersQuery.data?.inactivityCounters || {};
  const heatmap = heatmapQuery.data;
  const todHeatmap = todHeatmapQuery.data;
  const devices = devicesQuery.data;
  const timecardSummary = timecardSummaryQuery.data;
  const onlineCount = users.filter(u => u.isOnline).length;

  // Compute max for time-of-day heatmap intensity
  const maxTodValue = todHeatmap
    ? Math.max(1, ...todHeatmap.grid.flatMap(row => row))
    : 1;

  return (
    <div className="space-y-6">
      {!hideFilterBar && (
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Activity className="w-6 h-6 text-primary" />
          <h2 className="text-xl font-semibold">Activity Tracker</h2>
          {onlineCount > 0 && (
            <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/30 gap-1.5" data-testid="badge-online-count">
              <Circle className="w-2 h-2 fill-green-500 text-green-500" />
              {onlineCount} online
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Date-range presets */}
          <div className="flex items-center gap-1 flex-wrap" data-testid="bar-date-presets">
            {([
              { v: "today", label: "Today" },
              { v: "yesterday", label: "Yesterday" },
              { v: "this-week", label: "This Week" },
              { v: "7d", label: "Last 7 Days" },
              { v: "this-month", label: "This Month" },
              { v: "30d", label: "Last 30 Days" },
              { v: "custom", label: "Custom" },
            ] as Array<{ v: DateRange; label: string }>).map(({ v, label }) => (
              <Button
                key={v}
                size="sm"
                variant={dateRange === v ? "default" : "outline"}
                onClick={() => setDateRange(v)}
                data-testid={`button-preset-${v}`}
              >
                {label}
              </Button>
            ))}
          </div>

          {dateRange === "custom" && (
            <div className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="button-custom-start">
                    <CalendarDays className="w-4 h-4 mr-2" />
                    {customStart ? format(customStart, "MMM d") : "Start"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={customStart} onSelect={setCustomStart} />
                </PopoverContent>
              </Popover>
              <span className="text-muted-foreground">to</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="button-custom-end">
                    <CalendarDays className="w-4 h-4 mr-2" />
                    {customEnd ? format(customEnd, "MMM d") : "End"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={customEnd} onSelect={setCustomEnd} />
                </PopoverContent>
              </Popover>
            </div>
          )}

          {/* User filter */}
          <Select value={userFilter} onValueChange={setUserFilter}>
            <SelectTrigger className="w-44" data-testid="select-user-filter">
              <SelectValue placeholder="All users" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All users</SelectItem>
              {allUsers.map(u => (
                <SelectItem key={u.userId} value={u.userId} data-testid={`option-user-${u.userId}`}>
                  {u.userName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant={showComparison ? "default" : "outline"}
            size="sm"
            onClick={() => setShowComparison(!showComparison)}
            aria-pressed={showComparison}
            data-testid="button-toggle-comparison"
          >
            Compare Period
          </Button>
          {/* Screen-reader-only live region announces the toggle state so a
              VoiceOver/NVDA user knows the metric cards just gained or lost
              their period-over-period comparison numbers. The visual state
              change is obvious sighted (button fills in / metric cards
              re-render with deltas), but a non-sighted user otherwise gets
              no feedback that anything happened. */}
          <span
            role="status"
            aria-live="polite"
            className="sr-only"
            data-testid="status-comparison-toggle"
          >
            {showComparison
              ? "Period comparison enabled. Metric cards now show change vs the previous period."
              : "Period comparison disabled. Metric cards now show absolute values only."}
          </span>

          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCsv}
            data-testid="button-export-csv"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="New Leads"
          value={summary?.metrics.newLeads || 0}
          icon={Users}
          change={summary?.percentChanges?.newLeads}
          goal={summary?.goals?.newLeads?.daily}
          color="blue"
          testId="card-new-leads"
        />
        <MetricCard
          title="Clients Created"
          value={summary?.metrics.clientsCreated || 0}
          icon={Target}
          change={summary?.percentChanges?.clientsCreated}
          goal={summary?.goals?.clientsCreated?.daily}
          color="green"
          testId="card-clients-created"
        />
        <MetricCard
          title="Calls Logged"
          value={summary?.metrics.callsLogged || 0}
          icon={Phone}
          change={summary?.percentChanges?.callsLogged}
          goal={summary?.goals?.callsLogged?.daily}
          color="orange"
          testId="card-calls-logged"
        />
        <MetricCard
          title="Emails Sent"
          value={summary?.metrics.emailsSent || 0}
          icon={Mail}
          change={summary?.percentChanges?.emailsSent}
          goal={summary?.goals?.emailsSent?.daily}
          color="purple"
          testId="card-emails-sent"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Site Sessions"
          value={summary?.metrics.siteSessions || 0}
          icon={MonitorSmartphone}
          change={summary?.percentChanges?.siteSessions}
          color="blue"
          testId="card-site-sessions"
        />
        <MetricCard
          title="Total Active Time"
          value={formatActiveMs(summary?.metrics.totalActiveTimeMs || 0)}
          icon={Clock}
          color="teal"
          testId="card-total-active-time"
        />
        <MetricCard
          title="Avg Session Length"
          value={formatSessionMs(summary?.metrics.avgSessionLengthMs || 0)}
          icon={Timer}
          color="primary"
          testId="card-avg-session-length"
        />
        <MetricCard
          title="Reminders Set"
          value={summary?.metrics.remindersSet || 0}
          icon={Bell}
          change={summary?.percentChanges?.remindersSet}
          color="orange"
          testId="card-reminders-set"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Sessions Rolled"
          value={summary?.metrics.sessionsRolled || 0}
          icon={RotateCcw}
          color="teal"
          testId="card-sessions-rolled"
        />
        <MetricCard
          title="Invoices Resent"
          value={summary?.metrics.invoicesResent || 0}
          icon={FileText}
          change={summary?.percentChanges?.invoicesResent}
          color="orange"
          testId="card-invoices-resent"
        />
        <Card className="relative overflow-visible" data-testid="card-conversion-rate">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-500/10 text-green-500 flex items-center justify-center">
                <TrendingUp className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Conversion Rate</p>
                <p className="text-2xl font-bold">{summary?.metrics.conversionRate || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="card-active-drip-clients">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-500/10 text-purple-500 flex items-center justify-center">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active Drip Clients</p>
                <p className="text-2xl font-bold">{summary?.metrics.activeDripClients || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Timecard hours summary */}
      <Card data-testid="card-timecard-hours">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-500/10 text-blue-500 flex items-center justify-center">
              <Clock className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Timecard Hours</p>
              {timecardSummaryQuery.isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground mt-1" />
              ) : (
                <p className="text-2xl font-bold" data-testid="text-timecard-hours-total">
                  {formatActiveMs(timecardSummary?.totalClockedMs || 0)}
                </p>
              )}
            </div>
            <p className="text-xs text-muted-foreground ml-auto">
              Clocked in/out time for the selected range
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center justify-between gap-4 flex-wrap">
            <span>Inactivity Streaks</span>
            <div className="flex gap-2 flex-wrap">
              <Badge variant="outline" className={getInactivityColor("active")}>
                Active: {inactivityCounters["active"] || 0}
              </Badge>
              <Badge variant="outline" className={getInactivityColor("3+")}>
                3+ days: {inactivityCounters["3+"] || 0}
              </Badge>
              <Badge variant="outline" className={getInactivityColor("7+")}>
                7+ days: {inactivityCounters["7+"] || 0}
              </Badge>
              <Badge variant="outline" className={getInactivityColor("14+")}>
                14+ days: {inactivityCounters["14+"] || 0}
              </Badge>
              <Badge variant="outline" className={getInactivityColor("30+")}>
                30+ days: {inactivityCounters["30+"] || 0}
              </Badge>
            </div>
          </CardTitle>
        </CardHeader>
      </Card>

      <HeartbeatOutcomesCard data={heartbeatStatsQuery.data} isLoading={heartbeatStatsQuery.isLoading} />

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">User Activity</CardTitle>
          <p className="text-sm text-muted-foreground" data-testid="text-active-time-legend">
            <span className="font-medium text-foreground">Active Time</span> counts real engagement —
            the gaps between a user's interactions (clicks, typing, scrolling). Idle gaps longer than
            5 minutes are excluded (an open-but-untouched tab adds nothing), and continuous work is
            never capped. A non-stop stretch reads as one <span className="font-medium text-foreground">Session</span>;
            stepping away for 5+ minutes and returning adds a <span className="font-medium text-foreground">Roll</span>.
          </p>
        </CardHeader>
        <CardContent>
          {usersQuery.isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" />
                    <SortHeader field="userName">User</SortHeader>
                    <SortHeader field="totalActivity">Total</SortHeader>
                    <SortHeader field="leads">Leads</SortHeader>
                    <SortHeader field="clients">Clients</SortHeader>
                    <SortHeader field="calls">Calls</SortHeader>
                    <SortHeader field="emails">Emails</SortHeader>
                    <SortHeader field="sessions" title="Distinct continuous engagement blocks. A non-stop work stretch counts as one session; stepping away for more than 5 minutes starts a new one.">Sessions</SortHeader>
                    <SortHeader field="totalActiveTimeMs" title="Time the user was actively engaged (clicking, typing, scrolling). Sums the gaps between interactions; any idle gap longer than 5 minutes is excluded, so an open-but-untouched tab adds nothing. Continuous work is never capped.">Active Time</SortHeader>
                    <SortHeader field="avgSessionLengthMs" title="Average length of a continuous engagement block (Active Time ÷ Sessions).">Avg Session</SortHeader>
                    <TableHead title="Idle rolls — number of times the user stepped away for more than 5 minutes and then returned, starting a new session block.">Rolls</TableHead>
                    <TableHead title="Clocked hours from the timecard for this period. Includes any currently open shift.">Clocked</TableHead>
                    <SortHeader field="conversionRate">Conv %</SortHeader>
                    <SortHeader field="inactivityDays">Inactivity</SortHeader>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow
                      key={user.userId}
                      data-testid={`row-user-${user.userId}`}
                      className="cursor-pointer hover-elevate"
                      onClick={() => setDrillDownUser({ userId: user.userId, userName: user.userName })}
                    >
                      <TableCell className="pr-0">
                        {user.isOnline ? (
                          <span
                            title="Online (active in last 5 minutes)"
                            className="inline-block w-2.5 h-2.5 rounded-full bg-green-500"
                            data-testid={`indicator-online-${user.userId}`}
                          />
                        ) : (
                          <span className="inline-block w-2.5 h-2.5 rounded-full bg-muted" />
                        )}
                      </TableCell>
                      <TableCell>
                        <button
                          className="text-left hover:underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDrillDownUser({ userId: user.userId, userName: user.userName });
                          }}
                          data-testid={`button-drilldown-${user.userId}`}
                        >
                          <p className="font-medium">{user.userName}</p>
                          <p className="text-xs text-muted-foreground">{user.role}</p>
                        </button>
                      </TableCell>
                      <TableCell className="font-semibold">{user.totalActivity}</TableCell>
                      <TableCell>{user.leads}</TableCell>
                      <TableCell>{user.clients}</TableCell>
                      <TableCell>{user.calls}</TableCell>
                      <TableCell>{user.emails}</TableCell>
                      <TableCell>{user.sessions}</TableCell>
                      <TableCell data-testid={`text-active-time-${user.userId}`}>
                        {formatActiveMs(user.totalActiveTimeMs || 0)}
                      </TableCell>
                      <TableCell data-testid={`text-avg-session-${user.userId}`}>
                        {formatSessionMs(user.avgSessionLengthMs || 0)}
                      </TableCell>
                      <TableCell data-testid={`text-rolls-${user.userId}`} className="text-muted-foreground">
                        {user.sessionsRolled || 0}
                      </TableCell>
                      <TableCell data-testid={`text-clocked-${user.userId}`} className="text-muted-foreground tabular-nums">
                        {timecardSummary
                          ? formatActiveMs(timecardSummary.perUser[user.userId] || 0)
                          : "—"}
                      </TableCell>
                      <TableCell>{user.conversionRate}%</TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={getInactivityColor(user.inactivityBucket)}
                        >
                          {user.inactivityDays === 999 ? "Never" :
                            user.inactivityDays === 0 ? "Today" :
                              `${user.inactivityDays}d`}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                  {users.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={14} className="text-center text-muted-foreground py-8" data-testid="text-no-user-activity">
                        No user activity found for this period
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Time-of-Day Heatmap */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Time-of-Day Heatmap</CardTitle>
          <p className="text-sm text-muted-foreground">Active minutes by hour and day of week</p>
        </CardHeader>
        <CardContent>
          {todHeatmapQuery.isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : todHeatmap && todHeatmap.grid.some(r => r.some(v => v > 0)) ? (
            <div className="overflow-x-auto" data-testid="grid-time-of-day">
              <div className="min-w-max">
                <div className="flex gap-0.5 mb-1 ml-12">
                  {Array.from({ length: 24 }).map((_, hour) => (
                    <div key={hour} className="w-6 text-center text-[10px] text-muted-foreground">
                      {hour % 3 === 0 ? hour : ""}
                    </div>
                  ))}
                </div>
                {DAYS_OF_WEEK.map((dayName, dayIdx) => (
                  <div key={dayIdx} className="flex gap-0.5 mb-0.5 items-center">
                    <div className="w-12 text-xs text-muted-foreground pr-2">{dayName}</div>
                    {Array.from({ length: 24 }).map((_, hour) => {
                      const v = todHeatmap.grid[dayIdx]?.[hour] || 0;
                      const intensity = v === 0 ? 0 : Math.min(v / maxTodValue, 1);
                      return (
                        <div
                          key={hour}
                          className={cn(
                            "w-6 h-6 rounded",
                            v === 0 ? "bg-muted" : "bg-primary"
                          )}
                          style={{ opacity: v === 0 ? 0.2 : 0.25 + intensity * 0.75 }}
                          title={`${dayName} ${hour}:00 — ${v}m active`}
                          data-testid={`tod-cell-${dayIdx}-${hour}`}
                        />
                      );
                    })}
                  </div>
                ))}
                <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                  <span>Less</span>
                  <div className="flex gap-1">
                    {[0.2, 0.4, 0.6, 0.8, 1].map((opacity, i) => (
                      <div key={i} className="w-3 h-3 rounded bg-primary" style={{ opacity }} />
                    ))}
                  </div>
                  <span>More</span>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8" data-testid="text-no-tod-data">
              No active time recorded for this range.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Device & Browser Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card data-testid="card-browser-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Browser Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            {devices && devices.browsers.length > 0 ? (
              <div className="space-y-2">
                {devices.browsers.map(b => (
                  <div key={b.name} className="flex items-center gap-3" data-testid={`row-browser-${b.name}`}>
                    <div className="w-20 text-sm">{b.name}</div>
                    <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-primary" style={{ width: `${b.share}%` }} />
                    </div>
                    <div className="w-20 text-right text-sm tabular-nums text-muted-foreground">
                      {b.count} · {b.share}%
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-6" data-testid="text-no-browser-data">
                No session data for this range.
              </p>
            )}
          </CardContent>
        </Card>

        <Card data-testid="card-device-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Device Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            {devices && devices.devices.length > 0 ? (
              <div className="space-y-2">
                {devices.devices.map(d => (
                  <div key={d.name} className="flex items-center gap-3" data-testid={`row-device-${d.name}`}>
                    <div className="w-20 text-sm">{d.name}</div>
                    <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-teal-500" style={{ width: `${d.share}%` }} />
                    </div>
                    <div className="w-20 text-right text-sm tabular-nums text-muted-foreground">
                      {d.count} · {d.share}%
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-6" data-testid="text-no-device-data">
                No session data for this range.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Existing per-user heatmap (kept) */}
      {heatmap && heatmap.users.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">User Activity Heatmap</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <div className="min-w-max">
                <div className="flex gap-1 mb-2">
                  <div className="w-24 shrink-0" />
                  {heatmap.dates.slice(-14).map(date => (
                    <div key={date} className="w-8 text-center text-xs text-muted-foreground">
                      {format(new Date(date), "d")}
                    </div>
                  ))}
                </div>
                {heatmap.users.slice(0, 10).map(user => (
                  <div key={user.userId} className="flex gap-1 mb-1">
                    <div className="w-24 shrink-0 text-sm truncate" title={user.userName}>
                      {user.userName}
                    </div>
                    {heatmap.dates.slice(-14).map(date => {
                      const count = user.days[date] || 0;
                      const intensity = count === 0 ? 0 : Math.min(count / 5, 1);
                      return (
                        <div
                          key={date}
                          className={cn(
                            "w-8 h-6 rounded",
                            count === 0 ? "bg-muted" : "bg-primary"
                          )}
                          style={{ opacity: count === 0 ? 0.2 : 0.3 + intensity * 0.7 }}
                          title={`${user.userName}: ${count} activities on ${format(new Date(date), "MMM d")}`}
                        />
                      );
                    })}
                  </div>
                ))}
                <div className="flex items-center gap-2 mt-4 text-xs text-muted-foreground">
                  <span>Less</span>
                  <div className="flex gap-1">
                    {[0.2, 0.4, 0.6, 0.8, 1].map((opacity, i) => (
                      <div
                        key={i}
                        className="w-4 h-4 rounded bg-primary"
                        style={{ opacity }}
                      />
                    ))}
                  </div>
                  <span>More</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <ActivityUserDrillDown
        open={!!drillDownUser}
        userId={drillDownUser?.userId || null}
        userName={drillDownUser?.userName || null}
        start={start}
        end={end}
        onOpenChange={(open) => { if (!open) setDrillDownUser(null); }}
      />
    </div>
  );
}
